import React, { FC, useEffect } from 'react'

import { priceToString } from '@smarty-nx/code-utils'
import { GenericBannerItem, getSmartyProductSim } from '@smarty-nx/contentful'
import { Price } from '@smarty-nx/data'
import { colors, TrustPilot } from '@smarty-nx/ds'
import { TRACKING_CATEGORIES } from '@smarty-nx/tracking'
import { graphql, HeadProps, PageProps } from 'gatsby'

import { planDetailsContent } from './content'
import classes from './plan-details.module.scss'
import {
  renderAllPlansInclude,
  renderCoverage,
  renderDiscountCard,
  renderQuote,
  renderTimeline,
  renderUnlimitedBanner,
  renderWhySmarty,
} from './renderComponents'
import ColorButton from '../../components/ColorButton'
import CountdownBanner from '../../components/CountdownBanner'
import PageHead from '../../components/PageHead'
import PlanDetailsBanner from '../../components/PlanDetailsBanner'
import PlanSection2 from '../../components/PlanSectionPhase2'
import {
  BlockContainer,
  Centered,
  SiteContainer,
  Title,
} from '../../components/shared'
import spacerClasses from '../../components/styles/spacer.module.scss'
import withApp, { AppProps } from '../../components/withApp/withApp'
import { CONFIG, FEATURES } from '../../config'
import {
  createFAQPageSchema,
  jsonLdOrganization,
  mapAwardsItemsToSchemaAwards,
  mapFaqItemsToSchemaItems,
} from '../../constants/structuredData'
import FaqSection from '../../modular-system/FaqSection'
import { AwardsSection } from '../../modules/page-section'
import { ApiPlanNode, mapApiPlanNodesToPlans } from '../../utils/buildTimePlans'
import { productDmpgAttributes } from '../../utils/dmpgAttributes'
import { voucherWithCountdown } from '../../utils/getVoucher'
import { matchPlanMeta } from '../../utils/plans'

type PlanPageData = Queries.PlanPageQuery & {
  allApiPlan?: {
    nodes: ApiPlanNode[]
  }
}

type PlanPageContext = { slug: string; apiPlan: ApiPlanNode | null }

type PlanDetailsProps = AppProps & PageProps<PlanPageData, PlanPageContext>

const getPriceInDigits = (price: Price) => {
  const formattedPrice = priceToString(price)
  return formattedPrice.substring(1)
}

const PlanDetails: FC<PlanDetailsProps> = (props) => {
  const {
    voucherCode,
    pageContext: { slug },
    plans: { plans: originalPlans },
    groupPlans,
    fetchGroupPlans,
    data: { faq, awardsSection, allApiPlan },
  } = props

  const livePlans = (voucherCode ? originalPlans : groupPlans?.plans) || []
  // Redux plans are empty pre-hydration (and during build/SSR), so fall back
  // to build-time base pricing for every plan (not just this page's own
  // slug) — this is what keeps the "other plans" section below fully
  // populated in the server-rendered HTML, instead of showing only the
  // current plan until the client-side plans fetch resolves.
  const buildTimePlans = mapApiPlanNodesToPlans(allApiPlan?.nodes ?? [])
  const plans = livePlans.length > 0 ? livePlans : buildTimePlans
  const result = matchPlanMeta(plans, slug)
  const priceData = result ? result.priceData : null
  const plan = result ? result.planMeta : null
  const isDataOnly = plan?.plan_is_data_only

  useEffect(() => {
    fetchGroupPlans({})
  }, [fetchGroupPlans])

  const countdown = voucherWithCountdown(plans)

  if (!priceData || !plan) {
    return null
  }

  return (
    <div
      className={classes.siteWrapper}
      {...productDmpgAttributes(plan, priceData)}
      data-module-id={plans.length > 0 ? `pdp-${plan.slug}` : null}
      data-module_name="plans-selection"
      data-module_placement="pdp-other-plans-section"
      data-product-details_id={plan.slug}
    >
      {countdown && countdown.planSlug === plan.slug && (
        <CountdownBanner
          planName={countdown.planName}
          planSlug={countdown.planSlug}
          voucherExpiryDate={countdown.voucherExpiryDate}
          discountedPrice={countdown.discountedPrice}
          originalPrice={countdown.originalPrice}
          dataAllowanceGB={countdown.dataAllowanceGB}
          dataAllowanceWithPromosGB={countdown.dataAllowanceWithPromosGB}
        />
      )}

      <SiteContainer>
        <section className={classes.pageSection}>
          <Title
            center
            marginTop="8px"
            marginBottom="24px"
            weight="700"
            data-testid="plan-page-heading"
          >
            {planDetailsContent.commitmentFreeTitle}
          </Title>

          <div role="region" aria-live="polite">
            <PlanDetailsBanner
              planMeta={plan}
              priceData={priceData}
              buyBtnTitle={isDataOnly ? `Get the ${plan.data} plan` : undefined}
            />
          </div>

          <Centered style={{ maxWidth: '384px' }} margin="40px auto 16px">
            <ColorButton
              href={`/plans/${slug}#plans`}
              data-testid="view-all-plans-link"
              linkStyle
              foreColor="#fff"
              customStyle={{
                backgroundColor: colors['UI/DarkGrey'],
              }}
              /* wide - 384px max desktop */
              wide
              data-item-category={TRACKING_CATEGORIES.NAVIGATION}
              data-item-name={planDetailsContent.ctaViewAllDataName}
              to={undefined}
              color={undefined}
              onClick={undefined}
              ariaLabel={undefined}
              fullWidth={undefined}
              centered={undefined}
              newTab={undefined}
              className={undefined}
              eventName={undefined}
              eventProductId={undefined}
              eventCheckoutType={undefined}
              custStyle={undefined}
            >
              {planDetailsContent.ctaViewAll}
            </ColorButton>
          </Centered>

          {renderAllPlansInclude(plan)}
        </section>

        <div className={classes.payPalWrapper}>
          <img
            src="/assets/images/third-party/accepting-paypal.png"
            alt="Now accepting PayPal"
            loading="lazy"
            decoding="async"
          />
        </div>
        <TrustPilot
          reviewTag={
            FEATURES.TRUSTPILOT_UPDATE
              ? 'SMARTY_Homepage_Reviews'
              : 'websitewidget'
          }
          featureTrustPilot={FEATURES.TRUSTPILOT_UPDATE}
        />
        {renderTimeline(plan, priceData)}
      </SiteContainer>

      {slug === 'unlimited' && renderUnlimitedBanner()}

      {plan.data_discount && renderDiscountCard()}

      {renderQuote()}
      {renderCoverage()}
      {renderWhySmarty()}
      {awardsSection && <AwardsSection {...awardsSection} />}

      <div
        className={`${spacerClasses.spacer2xTop} ${spacerClasses.spacerBottom}`}
      >
        <BlockContainer
          style={{
            backgroundColor: colors['UI/DarkGrey'],
          }}
        >
          <SiteContainer>
            <PlanSection2 currentPlan={plan ?? undefined} plans={plans} />
          </SiteContainer>
        </BlockContainer>
      </div>

      <SiteContainer
        className={`${spacerClasses.spacerTop} ${spacerClasses.spacerBottom}`}
      >
        {faq && <FaqSection {...faq} />}
      </SiteContainer>
    </div>
  )
}

export default withApp(PlanDetails)

export const Head: FC<HeadProps<PlanPageData, PlanPageContext>> = ({
  data: { faq, awardsSection, allApiPlan },
  pageContext: { slug },
  location,
}) => {
  // Head has no access to Redux (no wrapRootElement context), so plan
  // metadata always comes from build-time API data here — this mirrors
  // what the page body itself falls back to during SSR anyway, since
  // Redux is empty pre-hydration.
  const buildTimePlans = mapApiPlanNodesToPlans(allApiPlan?.nodes ?? [])
  const result = matchPlanMeta(buildTimePlans, slug)
  const priceData = result ? result.priceData : null
  const plan = result ? result.planMeta : null
  const voucher = priceData ? priceData.voucher || null : null
  const banner = getSmartyProductSim() as GenericBannerItem

  const organizationAwards = mapAwardsItemsToSchemaAwards(
    awardsSection?.items || []
  )

  const reusableJsonProduct = {
    '@context': 'http://schema.org',
    '@type': 'Product',
    name: plan?.name,
    url: `${CONFIG.ENV_URL}/plans/${plan?.slug}`,
    image: banner?.image?.url,
    brand: {
      '@type': 'Organization',
      name: 'SMARTY',
    },
    ...(organizationAwards.length > 0 ? { award: organizationAwards } : {}),
  }

  let jsonLdProduct

  if (priceData && voucher && voucher.plan_card_heading) {
    jsonLdProduct = {
      ...reusableJsonProduct,
      description: voucher.plan_card_heading,
      offers: {
        '@type': 'Offer',
        priceCurrency: 'GBP',
        price: getPriceInDigits(priceData.finalPriceWithoutCredit),
        seller: {
          '@type': 'Organization',
          name: 'SMARTY',
        },
      },
    }
  } else if (priceData?.description && priceData.originalPrice.value) {
    jsonLdProduct = {
      ...reusableJsonProduct,
      description: priceData.description,
      offers: {
        '@type': 'Offer',
        priceCurrency: 'GBP',
        price: getPriceInDigits(priceData.originalPrice),
        seller: {
          '@type': 'Organization',
          name: 'SMARTY',
        },
      },
    }
  }

  const faqSchema = createFAQPageSchema(
    mapFaqItemsToSchemaItems(faq?.items || [])
  )

  return (
    <PageHead
      title={plan?.title ?? plan?.name ?? ''}
      description={plan?.description ?? ''}
      location={location}
      preventTitleTemplate
    >
      {slug === 'mini' && <meta name="robots" content="noindex, nofollow" />}

      {jsonLdProduct && (
        <script type="application/ld+json">
          {JSON.stringify(jsonLdProduct)}
        </script>
      )}

      {faqSchema && (
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
      )}

      <script type="application/ld+json">
        {JSON.stringify(jsonLdOrganization)}
      </script>
    </PageHead>
  )
}

export const query = graphql`
  query PlanPage {
    faq: contentfulPageSection(sectionName: { eq: "default-faq-list" }) {
      ...FaqSection
    }
    awardsSection: contentfulPageSection(sectionName: { eq: "awards" }) {
      ...GenericPageSection
    }
    allApiPlan {
      nodes {
        ...ApiPlanCarouselFields
      }
    }
  }
`
